import { capitalize, isNil } from 'lodash-es'

import type { EmployerVenueSearchState } from 'syft-acp-core/store/employer-venue-search/types'

import type {
  EmployerAccountPermission,
  EmployerAccountSummary,
  LocationsSummary,
  VenueSearchView,
} from './EmployerAccountsList.types'

// Company-wide permissions have no location. An account with no permissions at all
// comes back as a single location-less row with no codes, which isn't company-wide.
const isCompanyWide = (permission: EmployerAccountPermission) =>
  isNil(permission.work_location_id) && permission.codes.length > 0

export const locationsSummary = ({ level, permissions = [] }: EmployerAccountSummary): LocationsSummary => {
  if (level === 'main') return { label: 'All (main account)', companyWide: false }
  if (level === 'disabled') return { label: '–', companyWide: false }

  const locationCount = new Set(
    permissions.filter(p => !isNil(p.work_location_id)).map(p => p.work_location_id),
  ).size
  const companyWide = permissions.some(isCompanyWide)

  if (locationCount === 0) {
    return { label: companyWide ? 'Company-wide only' : 'No locations', companyWide: false }
  }

  return {
    label: `${locationCount.toLocaleString('en-GB')} location${locationCount === 1 ? '' : 's'}`,
    companyWide,
  }
}

export const accessAtVenue = ({ permissions = [] }: EmployerAccountSummary, venueID: string | number) => {
  const venuePermission = permissions.find(p => String(p.work_location_id) === String(venueID))

  return venuePermission
    ? venuePermission.codes.map(code => capitalize(code.replace(/_/g, ' '))).join(', ')
    : ''
}

// The search state is shared, so it can still hold another query's or another employer's
// results; only the ones for this employer and query count as an answer.
export const venueSearchView = (
  search: EmployerVenueSearchState,
  employerID: number,
  query: string,
): VenueSearchView => {
  const isAnswer = search.employerID === employerID && search.query === query
  const failed = isAnswer && search.status === 'failed'
  // The previous page stays listed while the next one loads, but not once the next one fails.
  const venues = search.employerID === employerID && !failed ? search.venues : []
  const succeeded = isAnswer && search.status === 'succeeded'

  return {
    venues,
    isSearching: !isAnswer || search.status === 'loading' || search.status === 'idle',
    failed,
    noMatches: succeeded && venues.length === 0,
    hiddenMatches: succeeded ? Math.max(search.total - venues.length, 0) : 0,
  }
}
